import { useState } from "react";
import { Search } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import SkylineHero from "@/components/SkylineHero";

const FAQ = () => {
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");

  const categories = [
    { id: "all", name: t('faq.categories.all') },
    { id: "general", name: t('faq.categories.general') },
    { id: "services", name: t('faq.categories.services') },
    { id: "partnership", name: t('faq.categories.partnership') },
    { id: "technology", name: t('faq.categories.technology') }
  ];

  const allFaqs = [
    {
      category: "general",
      question: t('faq.q1'),
      answer: t('faq.a1')
    },
    {
      category: "general",
      question: t('faq.q2'),
      answer: t('faq.a2')
    },
    {
      category: "services",
      question: t('faq.q3'),
      answer: t('faq.a3')
    },
    {
      category: "services",
      question: t('faq.q4'),
      answer: t('faq.a4')
    },
    {
      category: "services",
      question: t('faq.q5'),
      answer: t('faq.a5')
    },
    {
      category: "services",
      question: t('faq.q6'),
      answer: t('faq.a6')
    },
    {
      category: "services",
      question: t('faq.q7'),
      answer: t('faq.a7')
    },
    {
      category: "general",
      question: t('faq.q8'),
      answer: t('faq.a8')
    },
    {
      category: "general",
      question: t('faq.q9'),
      answer: t('faq.a9')
    },
    {
      category: "partnership",
      question: t('faq.q10'),
      answer: t('faq.a10')
    },
    {
      category: "partnership",
      question: t('faq.q11'),
      answer: t('faq.a11')
    },
    {
      category: "general",
      question: t('faq.q12'),
      answer: t('faq.a12')
    }
  ];

  const filteredFaqs = allFaqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || faq.category === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-12 sm:py-16 bg-gradient-to-br from-gray-light to-white relative overflow-hidden">
          {/* Skyline drawing animation — matches hotels hero */}
          <SkylineHero className="opacity-20" />
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-black mb-4 sm:mb-6">
                {t('faq.pageTitle')}
              </h1>
              <p className="text-lg sm:text-xl text-charcoal max-w-2xl mx-auto mb-6 sm:mb-8">
                {t('faq.pageSubtitle')}
              </p>
              
              {/* Search Bar */}
              <div className="relative max-w-md mx-auto">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-charcoal h-5 w-5" />
                <Input
                  type="text"
                  placeholder={t('faq.searchPlaceholder')}
                  className="pl-10 pr-4 py-3 text-lg"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Content */}
        <section className="py-12 sm:py-16">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-4 gap-6 sm:gap-8">
                {/* Categories Sidebar */}
                <div className="lg:col-span-1">
                  <div className="sticky top-24">
                    <h3 className="text-lg font-bold text-black mb-4">
                      {t('faq.categories.title')}
                    </h3>
                    <div className="space-y-2">
                      {categories.map((category) => (
                        <button
                          key={category.id}
                          onClick={() => setSelectedCategory(category.id)}
                          className={`w-full text-left px-4 py-2 rounded-lg transition-colors ${
                            selectedCategory === category.id
                              ? 'bg-black text-white'
                              : 'bg-gray-light text-charcoal hover:bg-gray-light/80'
                          }`}
                        >
                          {category.name}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* FAQ List */}
                <div className="lg:col-span-3">
                  {filteredFaqs.length > 0 ? (
                    <Accordion type="single" collapsible>
                      {filteredFaqs.map((faq, index) => (
                        <AccordionItem key={index} value={`item-${index}`} className="border-b border-gray-light mb-4">
                          <AccordionTrigger className="text-left text-black hover:text-charcoal font-medium py-6">
                            <div className="flex items-start gap-3">
                              <Badge variant="outline" className="mt-1 text-xs">
                                {categories.find(cat => cat.id === faq.category)?.name}
                              </Badge>
                              <span className="flex-1">{faq.question}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="text-charcoal leading-relaxed pb-6 pl-16">
                            {faq.answer}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-12">
                      <p className="text-charcoal text-lg">
                        {t('faq.noResults')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
};

export default FAQ;